<template>
  <Teleport to="body">
  <div class="modal fade" id="employeeSalaryModal" tabindex="-1" aria-labelledby="employeeSalaryModalLabel" aria-hidden="true" autofocus>
    <div class="modal-dialog modal-xl modal-dialog-scrollable esal-dialog">
      <div class="modal-content esal-modal-content">
        <!-- Hero header -->
        <div class="esal-hero">
          <div class="esal-hero-bg"></div>
          <div class="esal-hero-inner">
            <div class="esal-hero-left">
              <div class="esal-avatar">{{ employeeInitials }}</div>
              <div class="esal-hero-text">
                <p class="esal-hero-label">{{ isEditMode ? (hasExistingSalary ? 'Update salary' : 'Assign salary') : 'Salary details' }}</p>
                <h2 class="esal-hero-name" id="employeeSalaryModalLabel">{{ employeeData.name || 'Employee' }}</h2>
                <div class="esal-hero-chips">
                  <span v-if="employeeData.designation" class="esal-chip"><i class="bx bx-briefcase"></i> {{ employeeData.designation }}</span>
                  <span v-if="employeeData.department" class="esal-chip"><i class="bx bx-buildings"></i> {{ employeeData.department }}</span>
                  <span v-if="employeeData.hire_date" class="esal-chip"><i class="bx bx-calendar"></i> Joined {{ formatDate(employeeData.hire_date) }}</span>
                  <span v-if="employeeData.hire_date" class="esal-chip"><i class="bx bx-time-five"></i> {{ calculateServiceYears(employeeData.hire_date) }} yrs service</span>
                </div>
              </div>
            </div>
            <div class="esal-hero-actions">
              <button
                v-if="!loading && !isEditMode"
                type="button"
                class="esal-btn esal-btn-light"
                @click="toggleEditMode"
              >
                <i class="bx bx-edit"></i> Edit
              </button>
              <button
                v-else-if="!loading && isEditMode"
                type="button"
                class="esal-btn esal-btn-ghost"
                @click="toggleEditMode"
              >
                <i class="bx bx-x"></i> Cancel edit
              </button>
              <button type="button" class="esal-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="modal-body esal-body">
          <div v-if="loading" class="esal-loading">
            <div class="esal-spinner"></div>
            <p>Loading salary information…</p>
          </div>

          <div v-else class="esal-content">
            <form @submit.prevent="saveSalary">
              <!-- Notices -->
              <div v-if="isEditMode && !hasExistingSalary && displayMandatoryDeductions.length > 0" class="esal-notice info">
                <i class="bx bx-info-circle"></i>
                <div>
                  <strong>Automatic mandatory deductions</strong>
                  <p>These will be applied when you save:</p>
                  <ul>
                    <li v-for="deduction in displayMandatoryDeductions" :key="deduction.id">
                      {{ deduction.deduction_name }}<span v-if="deduction.description"> — {{ deduction.description }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="isEditMode && autoSelectedPayGrade" class="esal-notice success">
                <i class="bx bx-check-circle"></i>
                <div>
                  <strong>Pay grade auto-selected: {{ autoSelectedPayGrade.grade_name }}</strong>
                  <p>
                    Based on {{ formatCurrency(salaryForm.basic_salary) }} ·
                    Range {{ formatCurrency(autoSelectedPayGrade.min_salary) }} – {{ formatCurrency(autoSelectedPayGrade.max_salary) }}
                  </p>
                </div>
              </div>

              <div class="esal-grid-2">
                <section class="esal-panel">
                  <div class="esal-panel-head">
                    <i class="bx bx-wallet"></i>
                    <h3>Salary information</h3>
                  </div>
                  <div class="esal-panel-body">
                    <div class="esal-field">
                      <label>Basic salary <span class="esal-req">*</span></label>
                      <div class="esal-input-group">
                        <span class="esal-input-prefix">{{ getCurrencySymbol() }}</span>
                        <input
                          type="number"
                          class="esal-input"
                          v-model="salaryForm.basic_salary"
                          @input="onBasicSalaryChange"
                          :readonly="!isEditMode"
                          required
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        >
                      </div>
                      <p v-if="isEditMode && salaryForm.basic_salary && suggestedPayGrade" class="esal-hint success">
                        <i class="bx bx-bulb"></i>
                        Suggested: <strong>{{ suggestedPayGrade.grade_name }}</strong>
                        ({{ formatCurrency(suggestedPayGrade.min_salary) }} – {{ formatCurrency(suggestedPayGrade.max_salary) }})
                      </p>
                      <p v-else-if="isEditMode && salaryForm.basic_salary && !suggestedPayGrade" class="esal-hint warn">
                        <i class="bx bx-error"></i> No matching pay grade for this amount
                      </p>
                    </div>

                    <div class="esal-field">
                      <label>Pay grade</label>
                      <select
                        class="esal-input esal-select"
                        v-model="salaryForm.pay_grade_id"
                        :disabled="!isEditMode"
                        @change="onPayGradeManualChange"
                      >
                          <option value="">No Pay Grade</option>
                          <optgroup label="Regular Salary Grades" v-if="regularPayGrades.length > 0">
                            <option
                                v-for="grade in regularPayGrades"
                                :key="grade.id"
                                :value="grade.id"
                                :class="getPayGradeOptionClass(grade)"
                            >
                              {{ grade.grade_name }} ({{ formatCurrency(grade.min_salary) }} - {{ formatCurrency(grade.max_salary) }})
                            </option>
                          </optgroup>
                          <optgroup label="Other Grades" v-if="dsaPayGrades.length > 0">
                            <option
                                v-for="grade in dsaPayGrades"
                                :key="grade.id"
                                :value="grade.id"
                                :class="getPayGradeOptionClass(grade)"
                            >
                              {{ grade.grade_name }} ({{ formatCurrency(grade.min_salary) }})
                            </option>
                          </optgroup>
                        </select>
                      <p v-if="isEditMode && salaryForm.pay_grade_id && selectedPayGrade && !isSalaryInPayGradeRange" class="esal-hint danger">
                        <i class="bx bx-error-circle"></i>
                        Salary {{ formatCurrency(salaryForm.basic_salary) }} is outside range
                        ({{ formatCurrency(selectedPayGrade.min_salary) }} – {{ formatCurrency(selectedPayGrade.max_salary) }})
                      </p>
                      <p v-else-if="isEditMode && salaryForm.pay_grade_id && selectedPayGrade && isSalaryInPayGradeRange" class="esal-hint success">
                        <i class="bx bx-check-circle"></i> Salary is within pay grade range
                      </p>
                    </div>

                    <div class="esal-field-row">
                      <div class="esal-field">
                        <label>Effective date <span class="esal-req">*</span></label>
                        <input
                          type="date"
                          class="esal-input"
                          v-model="salaryForm.effective_date"
                          :readonly="!isEditMode"
                          required
                        >
                      </div>
                      <div class="esal-field">
                        <label>Frequency</label>
                        <select
                          class="esal-input esal-select"
                          v-model="salaryForm.payment_frequency"
                          :disabled="!isEditMode"
                        >
                          <option value="Monthly">Monthly</option>
                          <option value="Bi-weekly">Bi-weekly</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Daily">Daily</option>
                        </select>
                      </div>
                    </div>

                    <div class="esal-field">
                      <label>Currency</label>
                      <select
                        class="esal-input esal-select"
                        v-model="salaryForm.currency"
                        :disabled="!isEditMode"
                      >
                        <option value="KES">Kenyan Shilling (KES)</option>
                        <option value="USD">US Dollar (USD)</option>
                        <option value="GBP">British Pound (GBP)</option>
                        <option value="EUR">Euro (EUR)</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section class="esal-panel">
                  <div class="esal-panel-head">
                    <i class="bx bx-credit-card"></i>
                    <h3>Payment details</h3>
                  </div>
                  <div class="esal-panel-body">
                    <div class="esal-field">
                      <label>Payment method</label>
                      <select
                        class="esal-input esal-select"
                        v-model="salaryForm.payment_method"
                        :disabled="!isEditMode"
                      >
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Check">Check</option>
                          <option value="Cash">Cash</option>
                          <option value="Mobile Money">Mobile Money</option>
                        </select>
                      </div>

                    <template v-if="salaryForm.payment_method === 'Bank Transfer'">
                      <div class="esal-field">
                        <label>Bank name</label>
                        <input type="text" class="esal-input" v-model="salaryForm.bank_name" :readonly="!isEditMode">
                      </div>
                      <div class="esal-field-row">
                        <div class="esal-field">
                          <label>Bank code</label>
                          <input type="text" class="esal-input" v-model="salaryForm.bank_code" :readonly="!isEditMode" placeholder="e.g. 01">
                        </div>
                        <div class="esal-field">
                          <label>Branch code</label>
                          <input type="text" class="esal-input" v-model="salaryForm.branch_code" :readonly="!isEditMode">
                        </div>
                      </div>
                      <div class="esal-field">
                        <label>Account number</label>
                        <input type="text" class="esal-input" v-model="salaryForm.account_number" :readonly="!isEditMode">
                      </div>
                    </template>

                    <div v-if="salaryForm.payment_method === 'Mobile Money'" class="esal-field">
                      <label>Mobile money number</label>
                      <input type="text" class="esal-input" v-model="salaryForm.mobile_money_number" :readonly="!isEditMode">
                    </div>

                    <label v-if="isEditMode" class="esal-check">
                      <input type="checkbox" id="isActiveCheckbox" v-model="salaryForm.is_active">
                      <span>Active salary record</span>
                    </label>
                  </div>
                </section>
              </div>

              <!-- Pay grade info -->
              <section v-if="selectedPayGrade" class="esal-panel esal-panel-wide">
                <div class="esal-panel-head">
                  <i class="bx bx-star"></i>
                  <h3>Pay grade · {{ selectedPayGrade.grade_name }}</h3>
                </div>
                <div class="esal-panel-body">
                  <p v-if="selectedPayGrade.description" class="esal-grade-desc">{{ selectedPayGrade.description }}</p>
                  <div class="esal-grade-stats">
                    <div class="esal-grade-stat">
                      <span>Minimum</span>
                      <strong>{{ formatCurrency(selectedPayGrade.min_salary) }}</strong>
                    </div>
                    <div class="esal-grade-stat">
                      <span>Midpoint</span>
                      <strong>{{ formatCurrency(selectedPayGrade.midpoint_salary) }}</strong>
                    </div>
                    <div class="esal-grade-stat">
                      <span>Maximum</span>
                      <strong>{{ formatCurrency(selectedPayGrade.max_salary) }}</strong>
                    </div>
                  </div>
                  <div class="esal-grade-bar-wrap">
                    <div class="esal-grade-bar-track">
                      <div
                        class="esal-grade-bar-fill"
                        :class="getSalaryPositionClass()"
                        :style="{ width: getSalaryPositionPercentage() + '%' }"
                      ></div>
                    </div>
                    <span class="esal-grade-bar-label">Salary position in range · {{ getSalaryPositionPercentage() }}%</span>
                  </div>
                </div>
              </section>

              <!-- Salary history -->
              <section v-if="salaryHistory.length > 0" class="esal-panel esal-panel-wide">
                <div class="esal-panel-head">
                  <i class="bx bx-history"></i>
                  <h3>Salary history</h3>
                </div>
                <div class="esal-panel-body esal-panel-body-flush">
                  <div class="esal-table-wrap">
                    <table class="esal-table">
                      <thead>
                        <tr>
                          <th>Effective</th>
                          <th>Basic salary</th>
                          <th>Pay grade</th>
                          <th>Change</th>
                          <th>By</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(history, index) in salaryHistory" :key="index">
                          <td>{{ formatDate(history.effective_date) }}</td>
                          <td class="esal-amount">{{ formatCurrency(history.basic_salary) }}</td>
                          <td>{{ history.pay_grade_name || '—' }}</td>
                          <td><span :class="getSalaryChangeClass(history, index)">{{ formatSalaryChange(history, index) }}</span></td>
                          <td>{{ history.created_by }}</td>
                          <td class="esal-muted">{{ history.notes || '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <!-- Allowances & deductions -->
              <div class="esal-grid-2">
                <section class="esal-panel">
                  <div class="esal-panel-head">
                    <i class="bx bx-trending-up"></i>
                    <h3>Allowances</h3>
                    <button v-if="isEditMode" type="button" class="esal-btn esal-btn-sm primary" @click="showAddAllowanceModal">
                      <i class="bx bx-plus"></i> Add
                    </button>
                  </div>
                  <div class="esal-panel-body">
                    <div v-if="!employeeAllowances.length" class="esal-empty-inline">
                      <i class="bx bx-plus-circle"></i>
                      <p>No allowances configured</p>
                    </div>
                    <div v-else class="esal-item-list">
                      <div v-for="allowance in employeeAllowances" :key="allowance.id" class="esal-item-row">
                        <div>
                          <strong>{{ allowance.allowance_name }}</strong>
                          <span class="esal-pill" :class="allowance.is_taxable ? 'green' : 'amber'">
                            {{ allowance.is_taxable ? 'Taxable' : 'Non-taxable' }}
                          </span>
                        </div>
                        <div class="esal-item-row-end">
                          <span class="esal-amount earn">
                            +{{ allowance.is_percentage === '1'
                              ? `${allowance.percentage}% (${formatCurrency(calculatePercentageAmount(allowance))})`
                              : formatCurrency(allowance.amount) }}
                          </span>
                          <button v-if="isEditMode" type="button" class="esal-icon-btn danger" @click="removeAllowance(allowance.id)">
                            <i class="bx bx-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section class="esal-panel">
                  <div class="esal-panel-head">
                    <i class="bx bx-trending-down"></i>
                    <h3>Deductions</h3>
                    <button v-if="isEditMode" type="button" class="esal-btn esal-btn-sm primary" @click="showAddDeductionModal">
                      <i class="bx bx-plus"></i> Add
                    </button>
                  </div>
                  <div class="esal-panel-body">
                    <div v-if="!allDeductions.length" class="esal-empty-inline">
                      <i class="bx bx-minus-circle"></i>
                      <p>No deductions configured</p>
                    </div>
                    <div v-else class="esal-item-list">
                      <div v-for="deduction in allDeductions" :key="deduction.id || deduction.deduction_type_id" class="esal-item-row">
                        <div>
                          <strong>{{ deduction.deduction_name }}</strong>
                          <span v-if="isMandatoryDeduction(deduction)" class="esal-pill red">Mandatory</span>
                          <span v-else-if="!deduction.configured" class="esal-pill amber">Pending</span>
                          <span v-else class="esal-pill green">Active</span>
                        </div>
                        <div class="esal-item-row-end">
                          <span class="esal-amount ded" :class="{ muted: !deduction.configured }">
                            −{{ formatCurrency(getDeductionAmount(deduction)) }}
                          </span>
                          <button
                            v-if="isEditMode && !isMandatoryDeduction(deduction) && deduction.configured"
                            type="button"
                            class="esal-icon-btn danger"
                            @click="removeDeduction(deduction.id)"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                          <span v-else-if="isMandatoryDeduction(deduction)" class="esal-lock" title="Mandatory"><i class="bx bx-lock"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <!-- Salary breakdown -->
              <section class="esal-panel esal-panel-wide">
                <div class="esal-panel-head">
                  <i class="bx bx-calculator"></i>
                  <h3>Estimated breakdown</h3>
                </div>
                <div class="esal-panel-body">
                  <div class="esal-breakdown-grid">
                    <div class="esal-breakdown-lines">
                      <div class="esal-line"><span>Basic salary</span><strong>{{ formatCurrency(salaryForm.basic_salary) }}</strong></div>
                      <div class="esal-line earn"><span>Allowances</span><strong>+ {{ formatCurrency(calculateTotalAllowances()) }}</strong></div>
                      <div class="esal-line total"><span>Gross salary</span><strong>{{ formatCurrency(getGrossSalary()) }}</strong></div>
                      <div class="esal-line-section">Pre-tax deductions</div>
                      <div class="esal-line ded"><span>NSSF</span><strong>− {{ formatCurrency(calculateNSSFAmount(salaryForm.basic_salary)) }}</strong></div>
                      <div class="esal-line ded"><span>SHIF (2.75%)</span><strong>− {{ formatCurrency(calculateSHIF()) }}</strong></div>
                      <div class="esal-line ded"><span>Housing levy</span><strong>− {{ formatCurrency(calculateHousingLevy()) }}</strong></div>
                      <div class="esal-line total"><span>Taxable pay</span><strong>{{ formatCurrency(getTaxablePay()) }}</strong></div>
                      <div class="esal-line-section">Tax</div>
                      <div class="esal-line ded"><span>Income tax (PAYE)</span><strong>− {{ formatCurrency(calculateIncomeTax()) }}</strong></div>
                      <div class="esal-line earn"><span>Personal relief</span><strong>+ {{ formatCurrency(getPersonalRelief()) }}</strong></div>
                      <div class="esal-line ded"><span>Net PAYE</span><strong>− {{ formatCurrency(getNetPAYE()) }}</strong></div>
                      <div class="esal-line total"><span>Pay after tax</span><strong>{{ formatCurrency(getPayAfterTax()) }}</strong></div>
                      <div v-if="calculateOtherDeductions() > 0" class="esal-line ded">
                        <span>Other deductions</span><strong>− {{ formatCurrency(calculateOtherDeductions()) }}</strong>
                      </div>
                    </div>
                    <div class="esal-breakdown-aside">
                      <div class="esal-net-card">
                        <span class="esal-net-label">Estimated net salary</span>
                        <span class="esal-net-value">{{ formatCurrency(calculateNetSalary()) }}</span>
                        <span class="esal-net-sub">Finalized during payroll processing</span>
                      </div>
                      <div class="esal-notice info compact">
                        <i class="bx bx-info-circle"></i>
                        <div>
                          <strong>KRA estimate</strong>
                          <p>NSSF capped at KES 108,000 pensionable pay. SHIF 2.75% min KES 300. Housing levy 1.5% on gross.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section v-if="isEditMode" class="esal-panel esal-panel-wide">
                <div class="esal-panel-head">
                  <i class="bx bx-note"></i>
                  <h3>Notes</h3>
                </div>
                <div class="esal-panel-body">
                  <textarea
                    class="esal-input esal-textarea"
                    v-model="salaryForm.notes"
                    rows="3"
                    placeholder="Optional notes about this salary update…"
                  ></textarea>
                </div>
              </section>
            </form>
          </div>
        </div>

        <div v-if="isEditMode && !loading" class="esal-footer">
          <button type="button" class="esal-btn esal-btn-ghost-dark" @click="toggleEditMode">Cancel</button>
          <button type="button" class="esal-btn esal-btn-primary" :disabled="submitLoading" @click="saveSalary">
            <span v-if="submitLoading" class="esal-btn-spinner"></span>
            <i v-else class="bx bx-save"></i>
            {{ hasExistingSalary ? 'Update salary' : 'Save salary' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Allowance Modal -->
  <div class="modal fade esal-sub-modal" id="employeeSalaryAddAllowanceModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content esal-sub-content">
        <div class="esal-sub-header">
          <h5 class="esal-sub-title"><i class="bx bx-plus-circle"></i> Add allowance</h5>
          <button type="button" class="esal-close dark" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close">
            <i class="bx bx-x"></i>
          </button>
        </div>
        <div class="modal-body esal-sub-body">
          <form @submit.prevent="addAllowance">
            <div class="mb-3">
              <label class="form-label">Allowance Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="allowanceForm.allowance_type_id" required>
                <option value="">Select Allowance Type</option>
                <option v-for="type in allowanceTypes" :key="type.id" :value="type.id">
                  {{ type.allowance_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Amount Type <span class="text-danger">*</span></label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="amount-fixed"
                    value="fixed"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="amount-fixed">Fixed Amount</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="amount-percentage"
                    value="percentage"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="amount-percentage">Percentage of Basic Salary</label>
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'fixed'">
              <label class="form-label">Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                <input type="number" class="form-control" v-model="allowanceForm.amount" required min="0" step="0.01">
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'percentage'">
              <label class="form-label">Percentage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" v-model="allowanceForm.percentage" required min="0" max="100" step="0.01">
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Effective Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="allowanceForm.effective_date" required>
            </div>

            <div class="mb-3">
              <label class="form-label">End Date</label>
              <input type="date" class="form-control" v-model="allowanceForm.end_date">
              <small class="text-muted">Leave blank if this is a permanent allowance</small>
            </div>
          </form>
        </div>
        <div class="esal-sub-footer">
          <button type="button" class="esal-btn esal-btn-ghost-dark" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
          <button
            type="button"
            class="esal-btn esal-btn-primary"
            @click="addAllowance"
            :disabled="!isAllowanceFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="esal-btn-spinner"></span>
            Add allowance
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Deduction Modal -->
  <div class="modal fade esal-sub-modal" id="employeeSalaryAddDeductionModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content esal-sub-content">
        <div class="esal-sub-header">
          <h5 class="esal-sub-title"><i class="bx bx-minus-circle"></i> Add deduction</h5>
          <button type="button" class="esal-close dark" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close">
            <i class="bx bx-x"></i>
          </button>
        </div>
        <div class="modal-body esal-sub-body">
          <form @submit.prevent="addDeduction">
            <div class="mb-3">
              <label class="form-label">Deduction Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="deductionForm.deduction_type_id" required>
                <option value="">Select Deduction Type</option>
                <option v-for="type in deductionTypes" :key="type.id" :value="type.id">
                  {{ type.deduction_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Amount Type <span class="text-danger">*</span></label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="deduction-fixed"
                    value="fixed"
                    v-model="deductionForm.amount_type"
                >
                <label class="form-check-label" for="deduction-fixed">Fixed Amount</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="deduction-percentage"
                    value="percentage"
                    v-model="deductionForm.amount_type"
                >
                <label class="form-check-label" for="deduction-percentage">Percentage of Basic Salary</label>
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.amount_type === 'fixed'">
              <label class="form-label">Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                <input type="number" class="form-control" v-model="deductionForm.amount" required min="0" step="0.01">
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.amount_type === 'percentage'">
              <label class="form-label">Percentage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" v-model="deductionForm.percentage" required min="0" max="100" step="0.01">
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Effective Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="deductionForm.effective_date" required>
            </div>

            <div class="mb-3">
              <label class="form-label">End Date</label>
              <input type="date" class="form-control" v-model="deductionForm.end_date">
              <small class="text-muted">Leave blank if this is a permanent deduction</small>
            </div>
          </form>
        </div>
        <div class="esal-sub-footer">
          <button type="button" class="esal-btn esal-btn-ghost-dark" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
          <button
            type="button"
            class="esal-btn esal-btn-primary"
            @click="addDeduction"
            :disabled="!isDeductionFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="esal-btn-spinner"></span>
            Add deduction
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
/* eslint-disable */
import * as bootstrap from 'bootstrap';
import { authUrl, execute } from "@/api";

export default {
  name: 'EmployeeSalary',
  props: {
    employeeId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      isEditMode: false,
      submitLoading: false,
      hasExistingSalary: false,
      isManualPayGradeSelection: false,
      autoSelectedPayGrade: null,
      suggestedPayGrade: null,
      employeeData: {
        id: '',
        name: '',
        designation: '',
        department: '',
        hire_date: '',
        status: 'Active'
      },
      salaryForm: {
        basic_salary: 0,
        pay_grade_id: '',
        effective_date: '',
        payment_frequency: 'Monthly',
        payment_method: 'Bank Transfer',
        bank_name: '',
        bank_code: '',
        account_number: '',
        branch_code: '',
        mobile_money_number: '',
        currency: 'KES',
        is_active: true,
        notes: ''
      },
      salaryHistory: [],
      employeeAllowances: [],
      employeeDeductions: [],
      payGrades: [],
      allowanceTypes: [],
      deductionTypes: [],
      statutoryDeductions: [],
      deductionTypesMap: {},
      allowanceForm: {
        allowance_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      },
      deductionForm: {
        deduction_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      },
      modal: null,
      allowanceModal: null,
      deductionModal: null,
      mandatoryDeductions: [],
      needsToAddMandatoryDeductions: false
    };
  },
  computed: {
    employeeInitials() {
      const name = (this.employeeData.name || '').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },
    isAllowanceFormValid() {
      if (!this.allowanceForm.allowance_type_id) return false;
      if (this.allowanceForm.amount_type === 'fixed' && !this.allowanceForm.amount) return false;
      if (this.allowanceForm.amount_type === 'percentage' && !this.allowanceForm.percentage) return false;
      if (!this.allowanceForm.effective_date) return false;
      return true;
    },
    isDeductionFormValid() {
      if (!this.deductionForm.deduction_type_id) return false;
      if (this.deductionForm.amount_type === 'fixed' && !this.deductionForm.amount) return false;
      if (this.deductionForm.amount_type === 'percentage' && !this.deductionForm.percentage) return false;
      if (!this.deductionForm.effective_date) return false;
      return true;
    },
    allDeductions() {
      const existingDeductionMap = {};
      this.employeeDeductions.forEach(deduction => {
        const typeId = deduction.deduction_type_id || deduction.id;
        existingDeductionMap[typeId] = { ...deduction, configured: true };
      });

      const allDeductionsList = [];
      this.employeeDeductions.forEach(deduction => {
        allDeductionsList.push({ ...deduction, configured: true });
      });

      this.mandatoryDeductions.forEach(mandatoryDeduction => {
        const typeId = mandatoryDeduction.id;
        if (!existingDeductionMap[typeId]) {
          const pendingDeduction = {
            id: mandatoryDeduction.id,
            deduction_type_id: mandatoryDeduction.id,
            deduction_name: mandatoryDeduction.deduction_name,
            is_percentage: mandatoryDeduction.is_percentage,
            percentage: mandatoryDeduction.default_percentage,
            amount: mandatoryDeduction.default_amount,
            is_pretax: mandatoryDeduction.is_pretax,
            is_mandatory: mandatoryDeduction.is_mandatory,
            description: mandatoryDeduction.description,
            configured: false
          };
          allDeductionsList.push(pendingDeduction);
        }
      });

      return allDeductionsList;
    },

    displayMandatoryDeductions() {
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);
      return this.statutoryDeductions.filter(deduction =>
          (deduction.is_mandatory === "1" || deduction.is_mandatory === 1) &&
          !currentDeductionTypeIds.includes(deduction.id)
      );
    },

    selectedPayGrade() {
      if (!this.salaryForm.pay_grade_id) return null;
      return this.payGrades.find(grade => grade.id === this.salaryForm.pay_grade_id);
    },

    isSalaryInPayGradeRange() {
      if (!this.selectedPayGrade || !this.salaryForm.basic_salary) return false;
      const salary = parseFloat(this.salaryForm.basic_salary);
      const minSalary = parseFloat(this.selectedPayGrade.min_salary);
      const maxSalary = parseFloat(this.selectedPayGrade.max_salary);

      if (minSalary === maxSalary) {
        const tolerance = minSalary * 0.05;
        return salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance);
      }

      return salary >= minSalary && salary <= maxSalary;
    },

    regularPayGrades() {
      return this.payGrades.filter(grade => {
        const minSalary = parseFloat(grade.min_salary || 0);
        const maxSalary = parseFloat(grade.max_salary || 0);
        if (minSalary === 0 && maxSalary === 0) return false;
        const gradeType = (grade.grade_type || '').toLowerCase();
        return gradeType === 'salary' || gradeType === 'regular' || gradeType === '' || !grade.grade_type;
      });
    },

    dsaPayGrades() {
      return this.payGrades.filter(grade => {
        const minSalary = parseFloat(grade.min_salary || 0);
        const maxSalary = parseFloat(grade.max_salary || 0);
        if (minSalary === 0 && maxSalary === 0) return false;
        const gradeType = (grade.grade_type || '').toLowerCase();
        return gradeType === 'allowance' || gradeType === 'dsa' || gradeType === 'travel' || gradeType === 'other';
      });
    }
  },
  components: {
  },
  watch: {
    employeeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // ✅ FIX: Wait for DOM to be ready before fetching, so modal el exists
          this.$nextTick(() => {
            this.fetchEmployeeSalary(newVal);
          });
        }
      }
    }
  },
  mounted() {
    this.fetchPayGrades();
    this.fetchAllowanceTypes();
    this.fetchDeductionTypes();
    this.fetchStatutoryDeductions();
    // ✅ FIX: initModals called after DOM is ready
    this.$nextTick(() => {
      this.initModals();
    });
  },
  beforeUnmount() {
    // ✅ FIX: Clean up modal instances to prevent memory leaks and stale references
    this.destroyModals();
    this.cleanupModalArtifacts();
  },
  methods: {
    normalizeBootstrapLayers() {
      // Ensure any leftover backdrop never blocks interaction with the modal.
      document.querySelectorAll('.modal-backdrop').forEach((el) => {
        el.style.zIndex = '20050';
        el.style.pointerEvents = 'none';
      });

      const ids = [
        'employeeSalaryModal',
        'employeeSalaryAddAllowanceModal',
        'employeeSalaryAddDeductionModal'
      ];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.classList.contains('show')) {
          el.style.zIndex = '20060';
        }
      });
    },

    cleanupModalArtifacts() {
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    },

    // ✅ FIX: Separate destroy method to cleanly remove Bootstrap instances
    destroyModals() {
      try {
        if (this.modal) { this.modal.dispose(); this.modal = null; }
        if (this.allowanceModal) { this.allowanceModal.dispose(); this.allowanceModal = null; }
        if (this.deductionModal) { this.deductionModal.dispose(); this.deductionModal = null; }
      } catch (e) {
        // ignore dispose errors
      }
    },

    // ✅ FIX: getOrCreateModal helper — always returns a valid Bootstrap Modal instance
    getOrCreateModal(id, options = {}) {
      const el = document.getElementById(id);
      if (!el) return null;
      // Return existing instance if still valid, otherwise create a new one
      const existing = bootstrap.Modal.getInstance(el);
      if (existing) return existing;
      return new bootstrap.Modal(el, { backdrop: 'static', keyboard: false, ...options });
    },

    initModals() {
      const mainEl = document.getElementById('employeeSalaryModal');
      const allowanceEl = document.getElementById('employeeSalaryAddAllowanceModal');
      const deductionEl = document.getElementById('employeeSalaryAddDeductionModal');

      if (!mainEl || !allowanceEl || !deductionEl) {
        // DOM not ready yet, retry after a short delay
        setTimeout(() => this.initModals(), 100);
        return;
      }

      this.modal = new bootstrap.Modal(mainEl, { backdrop: 'static', keyboard: false });
      this.allowanceModal = new bootstrap.Modal(allowanceEl, { backdrop: 'static', keyboard: false });
      this.deductionModal = new bootstrap.Modal(deductionEl, { backdrop: 'static', keyboard: false });

      mainEl.addEventListener('shown.bs.modal', () => this.normalizeBootstrapLayers());
      allowanceEl.addEventListener('shown.bs.modal', () => this.normalizeBootstrapLayers());
      deductionEl.addEventListener('shown.bs.modal', () => this.normalizeBootstrapLayers());

      // ✅ FIX: Clear stale instance reference on hide so it gets recreated on next open
      mainEl.addEventListener('hidden.bs.modal', () => {
        this.submitLoading = false;
        this.isEditMode = false;
        this.modal = null;
        this.cleanupModalArtifacts();
        // ✅ FIX: Notify parent that modal is fully closed so it can reset selectedEmployeeId
        this.$emit('modal-closed');
      });

      allowanceEl.addEventListener('hidden.bs.modal', this.resetAllowanceForm);
      deductionEl.addEventListener('hidden.bs.modal', this.resetDeductionForm);
    },

    isPreTaxDeduction(deductionName) {
      const preTaxDeductions = ['NSSF', 'SHIF', 'Housing Levy', 'NHIF'];
      return preTaxDeductions.some(name => deductionName.includes(name));
    },

    getGrossSalary() {
      return parseFloat(this.salaryForm.basic_salary || 0) + this.calculateTotalAllowances();
    },

    getTaxablePay() {
      const gross = this.getGrossSalary();
      const nssf = this.calculateNSSFAmount(this.salaryForm.basic_salary);
      const shif = this.calculateSHIF();
      const housingLevy = this.calculateHousingLevy();
      return Math.max(0, gross - nssf - shif - housingLevy);
    },

    calculateIncomeTax() {
      const taxablePay = this.getTaxablePay();
      const payeInfo = this.deductionTypesMap['PAYE'];
      const tiers = Array.isArray(payeInfo?.tiers) && payeInfo.tiers.length > 0
          ? [...payeInfo.tiers]
          : [
            { min_amount: 0, max_amount: 24000, percentage: 10 },
            { min_amount: 24000, max_amount: 32333, percentage: 25 },
            { min_amount: 32333, max_amount: 500000, percentage: 30 },
            { min_amount: 500000, max_amount: 800000, percentage: 32.5 },
            { min_amount: 800000, max_amount: null, percentage: 35 }
          ];

      tiers.sort((a, b) => parseFloat(a.min_amount || 0) - parseFloat(b.min_amount || 0));

      const hasFixedAmounts = tiers.some(tier => parseFloat(tier.fixed_amount || 0) > 0);

      if (hasFixedAmounts) {
        for (const tier of tiers) {
          const minAmount = parseFloat(tier.min_amount || 0);
          const maxAmount = tier.max_amount ? parseFloat(tier.max_amount) : Infinity;
          const percentage = parseFloat(tier.percentage || 0) / 100;
          const fixedAmount = parseFloat(tier.fixed_amount || 0);

          if (taxablePay > minAmount && taxablePay <= maxAmount) {
            return fixedAmount + ((taxablePay - minAmount) * percentage);
          }
          if (taxablePay > minAmount && maxAmount === Infinity) {
            return fixedAmount + ((taxablePay - minAmount) * percentage);
          }
        }
        return 0;
      }

      let incomeTax = 0;
      for (const tier of tiers) {
        const minAmount = parseFloat(tier.min_amount || 0);
        const maxAmount = tier.max_amount ? parseFloat(tier.max_amount) : Infinity;
        const percentage = parseFloat(tier.percentage || 0) / 100;

        if (taxablePay <= minAmount) continue;
        const taxableInBracket = Math.min(taxablePay, maxAmount) - minAmount;
        incomeTax += taxableInBracket * percentage;

        if (taxablePay <= maxAmount) break;
      }

      return incomeTax;
    },

    getPersonalRelief() {
      const personalReliefInfo = this.deductionTypesMap['Personal Relief'];
      return personalReliefInfo ? Math.abs(parseFloat(personalReliefInfo.default_amount || 2400)) : 2400;
    },

    getNetPAYE() {
      return Math.max(0, this.calculateIncomeTax() - this.getPersonalRelief());
    },

    getPayAfterTax() {
      return this.getTaxablePay() - this.getNetPAYE();
    },

    calculateHousingLevy() {
      const gross = this.getGrossSalary();
      return gross * 0.015;
    },

    calculateSHIF() {
      const gross = this.getGrossSalary();
      const calculated = gross * 0.0275;
      return Math.max(300, calculated);
    },

    calculateOtherDeductions() {
      let total = 0;
      this.employeeDeductions.forEach(deduction => {
        const name = deduction.deduction_name;
        if (!name.includes('NSSF') && !name.includes('PAYE') &&
            !name.includes('SHIF') && !name.includes('Housing Levy') &&
            !name.includes('Personal Relief') && !name.includes('NHIF')) {
          total += this.getDeductionAmount(deduction);
        }
      });
      return total;
    },

    onBasicSalaryChange() {
      if (!this.isEditMode || this.isManualPayGradeSelection) return;
      const salary = parseFloat(this.salaryForm.basic_salary);
      if (!salary || salary <= 0) {
        this.suggestedPayGrade = null;
        this.autoSelectedPayGrade = null;
        this.salaryForm.pay_grade_id = '';
        return;
      }

      const matchingGrade = this.findPayGradeForSalary(salary);
      if (matchingGrade) {
        this.suggestedPayGrade = matchingGrade;
        this.autoSelectedPayGrade = matchingGrade;
        this.salaryForm.pay_grade_id = matchingGrade.id;
        this.isManualPayGradeSelection = false;
      } else {
        this.suggestedPayGrade = null;
        this.autoSelectedPayGrade = null;
        this.salaryForm.pay_grade_id = '';
      }
    },

    onPayGradeManualChange() {
      this.isManualPayGradeSelection = true;
      this.autoSelectedPayGrade = null;
      this.suggestedPayGrade = null;
    },

    findPayGradeForSalary(salary) {
      const regularGrades = this.regularPayGrades;
      const exactMatch = regularGrades.find(grade => {
        const minSalary = parseFloat(grade.min_salary);
        const maxSalary = parseFloat(grade.max_salary);

        if (minSalary === maxSalary) {
          const tolerance = minSalary * 0.05;
          return salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance);
        }
        return salary >= minSalary && salary <= maxSalary;
      });

      if (exactMatch) return exactMatch;

      const closestMatch = regularGrades.find(grade => {
        const minSalary = parseFloat(grade.min_salary);
        const maxSalary = parseFloat(grade.max_salary);
        const tolerance = (maxSalary - minSalary) * 0.1;
        return salary >= (minSalary - tolerance) && salary <= (maxSalary + tolerance);
      });

      return closestMatch || null;
    },

    getPayGradeOptionClass(grade) {
      const salary = parseFloat(this.salaryForm.basic_salary);
      if (!salary || !this.isEditMode) return '';

      const minSalary = parseFloat(grade.min_salary);
      const maxSalary = parseFloat(grade.max_salary);

      if (minSalary === 0 && maxSalary === 0) return 'text-muted';

      const gradeType = (grade.grade_type || '').toLowerCase();
      const isNonSalary = gradeType === 'allowance' || gradeType === 'dsa' || gradeType === 'travel' || gradeType === 'other';

      if (isNonSalary) return 'text-muted font-italic';

      if (minSalary === maxSalary) {
        const tolerance = minSalary * 0.05;
        if (salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance)) {
          return 'bg-light-success text-success';
        }
      } else if (salary >= minSalary && salary <= maxSalary) {
        return 'bg-light-success text-success';
      }

      return '';
    },

    getSalaryPositionClass() {
      if (!this.selectedPayGrade || !this.isSalaryInPayGradeRange) return 'bg-secondary';
      const percentage = this.getSalaryPositionPercentage();
      if (percentage <= 33) return 'bg-info';
      if (percentage <= 66) return 'bg-warning';
      return 'bg-success';
    },

    getSalaryPositionPercentage() {
      if (!this.selectedPayGrade || !this.salaryForm.basic_salary) return 0;

      const salary = parseFloat(this.salaryForm.basic_salary);
      const minSalary = parseFloat(this.selectedPayGrade.min_salary);
      const maxSalary = parseFloat(this.selectedPayGrade.max_salary);

      if (maxSalary === minSalary) return salary === minSalary ? 100 : 0;

      const percentage = ((salary - minSalary) / (maxSalary - minSalary)) * 100;
      return Math.min(100, Math.max(0, Math.round(percentage)));
    },

    // ✅ FIX: showModal now always gets or creates a fresh instance
    showModal() {
      this.$nextTick(() => {
        // Remove stale backdrops/modal-open state before opening.
        this.cleanupModalArtifacts();

        const modal = this.getOrCreateModal('employeeSalaryModal');
        if (modal) {
          this.modal = modal;

          // Re-attach hidden listener if we created a new instance
          const el = document.getElementById('employeeSalaryModal');
          if (el && !el._salaryHiddenListenerAttached) {
            el._salaryHiddenListenerAttached = true;
            el.addEventListener('hidden.bs.modal', () => {
              this.submitLoading = false;
              this.isEditMode = false;
              this.modal = null;
              this.cleanupModalArtifacts();
              // ✅ FIX: Reset flag so listener can be re-attached on next open
              const modalEl = document.getElementById('employeeSalaryModal');
              if (modalEl) modalEl._salaryHiddenListenerAttached = false;
              // ✅ FIX: Notify parent that modal is fully closed
              this.$emit('modal-closed');
            });
          }

          modal.show();
          setTimeout(() => this.normalizeBootstrapLayers(), 0);
        }
      });
    },

    hideModal() {
      const el = document.getElementById('employeeSalaryModal');
      if (!el) return;
      const instance = bootstrap.Modal.getInstance(el);
      if (instance) {
        instance.hide();
      } else if (this.modal) {
        this.modal.hide();
      }
      setTimeout(() => this.cleanupModalArtifacts(), 300);
    },

    closeModalFromEvent(ev) {
      this.submitLoading = false;

      if (ev && ev.target) {
        const target = ev.target;
        if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
          return;
        }
      }

      var targetElement = ev && ev.currentTarget;
      if (targetElement && targetElement.closest) {
        if (targetElement.closest('#employeeSalaryAddAllowanceModal')) {
          this.hideAddAllowanceModal();
          return;
        }
        if (targetElement.closest('#employeeSalaryAddDeductionModal')) {
          this.hideAddDeductionModal();
          return;
        }
      }
      this.hideModal();
    },

    showAddAllowanceModal() {
      const modal = this.getOrCreateModal('employeeSalaryAddAllowanceModal');
      if (modal) {
        this.allowanceModal = modal;
        modal.show();
        setTimeout(() => this.normalizeBootstrapLayers(), 0);
      }
    },

    hideAddAllowanceModal() {
      const el = document.getElementById('employeeSalaryAddAllowanceModal');
      if (!el) return;
      const instance = bootstrap.Modal.getInstance(el);
      if (instance) instance.hide();
    },

    showAddDeductionModal() {
      const modal = this.getOrCreateModal('employeeSalaryAddDeductionModal');
      if (modal) {
        this.deductionModal = modal;
        modal.show();
        setTimeout(() => this.normalizeBootstrapLayers(), 0);
      }
    },

    hideAddDeductionModal() {
      const el = document.getElementById('employeeSalaryAddDeductionModal');
      if (!el) return;
      const instance = bootstrap.Modal.getInstance(el);
      if (instance) instance.hide();
    },

    resetEditMode() {
      this.isEditMode = false;
      this.needsToAddMandatoryDeductions = false;
      this.isManualPayGradeSelection = false;
      this.autoSelectedPayGrade = null;
      this.suggestedPayGrade = null;
    },

    resetAllowanceForm() {
      this.allowanceForm = {
        allowance_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      };
    },

    resetDeductionForm() {
      this.deductionForm = {
        deduction_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      };
    },

    fetchStatutoryDeductions() {
      const data = new FormData();
      data.append("function", "getStatutoryDeductions");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.statutoryDeductions = res.data.data.statutoryDeductions;
              this.deductionTypesMap = {};
              this.statutoryDeductions.forEach(deduction => {
                this.deductionTypesMap[deduction.deduction_name] = deduction;
              });
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchEmployeeSalary(employeeId) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEmployeeSalary");
      data.append("employeeId", employeeId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              const responseData = res.data.data;
              this.employeeData = responseData.employee || this.employeeData;
              this.hasExistingSalary = !!(responseData.currentSalary && responseData.currentSalary.id);

              if (responseData.currentSalary) {
                this.salaryForm = {
                  basic_salary: responseData.currentSalary.basic_salary || 0,
                  pay_grade_id: responseData.currentSalary.pay_grade_id || '',
                  effective_date: responseData.currentSalary.effective_date || new Date().toISOString().split('T')[0],
                  payment_frequency: responseData.currentSalary.payment_frequency || 'Monthly',
                  payment_method: responseData.currentSalary.payment_method || 'Bank Transfer',
                  bank_name: responseData.currentSalary.bank_name || '',
                  bank_code: responseData.currentSalary.bank_code || '',
                  account_number: responseData.currentSalary.account_number || '',
                  branch_code: responseData.currentSalary.branch_code || '',
                  mobile_money_number: responseData.currentSalary.mobile_money_number || '',
                  currency: responseData.currentSalary.currency || 'KES',
                  is_active: responseData.currentSalary.is_active === '1' || responseData.currentSalary.is_active === 1 || responseData.currentSalary.is_active === true,
                  notes: responseData.currentSalary.notes || ''
                };
              } else {
                // ✅ FIX: Reset form for new salary assignment
                this.salaryForm = {
                  basic_salary: 0,
                  pay_grade_id: '',
                  effective_date: new Date().toISOString().split('T')[0],
                  payment_frequency: 'Monthly',
                  payment_method: 'Bank Transfer',
                  bank_name: '',
                  bank_code: '',
                  account_number: '',
                  branch_code: '',
                  mobile_money_number: '',
                  currency: 'KES',
                  is_active: true,
                  notes: ''
                };
              }

              this.salaryHistory = responseData.salaryHistory || [];
              this.employeeAllowances = responseData.allowances || [];
              this.employeeDeductions = responseData.deductions || [];

              this.checkMandatoryDeductions();
              if (!this.hasExistingSalary) {
                this.isEditMode = true;
              }
              // ✅ FIX: showModal called here, after data is ready
              this.showModal();
            } else {
              alert(res.data.message || "Failed to load employee salary information");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading employee salary information");
          });
    },

    fetchPayGrades() {
      const data = new FormData();
      data.append("function", "getPayGrades");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.payGrades = res.data.data.payGrades || [];
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchAllowanceTypes() {
      const data = new FormData();
      data.append("function", "getAllowanceTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.allowanceTypes = res.data.data.allowanceTypes || [];
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchDeductionTypes() {
      const data = new FormData();
      data.append("function", "getDeductionTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.deductionTypes = res.data.data.deductionTypes || [];
              this.mandatoryDeductions = this.deductionTypes.filter(deduction =>
                  deduction.is_mandatory === "1" || deduction.is_mandatory === 1
              );
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (this.isEditMode) {
        this.isManualPayGradeSelection = false;
        this.autoSelectedPayGrade = null;
        this.suggestedPayGrade = null;
        this.checkMandatoryDeductions();

        if (this.salaryForm.basic_salary) {
          this.$nextTick(() => {
            this.onBasicSalaryChange();
          });
        }
      }
    },

    checkMandatoryDeductions() {
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);
      const missingMandatory = this.mandatoryDeductions.filter(deduction =>
          !currentDeductionTypeIds.includes(deduction.id)
      );
      this.needsToAddMandatoryDeductions = missingMandatory.length > 0;
    },

    isMandatoryDeduction(deduction) {
      const deductionTypeId = deduction.deduction_type_id || deduction.id;
      return this.mandatoryDeductions.some(mandatory => mandatory.id === String(deductionTypeId));
    },

    getCurrencySymbol() {
      return this.salaryForm.currency || 'KES';
    },

    formatCurrency(value) {
      const currency = this.salaryForm.currency || 'KES';
      const numValue = parseFloat(value) || 0;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(numValue);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },

    calculateServiceYears(hireDate) {
      if (!hireDate) return 0;

      const hire = new Date(hireDate);
      const today = new Date();
      const diffTime = today - hire;
      const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);

      return Math.floor(diffYears * 10) / 10;
    },

    getSalaryChangeClass(history, index) {
      if (index === this.salaryHistory.length - 1) return '';

      const nextIndex = index - 1;
      if (nextIndex < 0 || nextIndex >= this.salaryHistory.length) return '';

      const currentSalary = parseFloat(history.basic_salary);
      const nextSalary = parseFloat(this.salaryHistory[nextIndex].basic_salary);

      if (currentSalary < nextSalary) {
        return 'text-danger';
      } else if (currentSalary > nextSalary) {
        return 'text-success';
      }

      return '';
    },

    formatSalaryChange(history, index) {
      if (index === this.salaryHistory.length - 1) return '';

      const nextIndex = index - 1;
      if (nextIndex < 0 || nextIndex >= this.salaryHistory.length) return '';

      const currentSalary = parseFloat(history.basic_salary);
      const nextSalary = parseFloat(this.salaryHistory[nextIndex].basic_salary);
      const diff = nextSalary - currentSalary;

      if (diff === 0) return 'No change';

      const percentChange = (diff / currentSalary) * 100;
      const sign = diff > 0 ? '+' : '';

      return `${sign}${diff.toFixed(2)} (${sign}${percentChange.toFixed(2)}%)`;
    },

    calculatePercentageAmount(item) {
      if (!item.is_percentage || !item.percentage) return 0;
      return (parseFloat(this.salaryForm.basic_salary) * parseFloat(item.percentage)) / 100;
    },

    calculateTotalAllowances() {
      let total = 0;
      this.employeeAllowances.forEach(allowance => {
        if (allowance.is_percentage === '1') {
          total += this.calculatePercentageAmount(allowance);
        } else {
          total += parseFloat(allowance.amount || 0);
        }
      });
      return total;
    },

    getDeductionAmount(deduction) {
      const deductionName = deduction.deduction_name;
      const basicSalary = parseFloat(this.salaryForm.basic_salary || 0);
      const grossSalary = basicSalary + this.calculateTotalAllowances();

      if (deductionName.includes('NSSF') && !deductionName.includes('Employer')) {
        return this.calculateNSSFAmount(basicSalary);
      }

      if (deductionName === 'SHIF' || deductionName.includes('SHIF')) {
        return this.calculateSHIF();
      }

      if (deductionName.includes('Housing Levy') && !deductionName.includes('Employer')) {
        return this.calculateHousingLevy();
      }

      if (deductionName === 'NHIF') {
        return this.calculateTieredDeduction('NHIF', grossSalary);
      }

      if (deductionName === 'PAYE') {
        return this.getNetPAYE();
      }

      if (deductionName === 'Personal Relief') {
        return 0;
      }

      if (deduction.is_percentage === '1') {
        let amount = this.calculatePercentageAmount(deduction);
        const statutoryInfo = this.deductionTypesMap[deductionName];
        if (statutoryInfo && statutoryInfo.max_contribution) {
          amount = Math.min(amount, parseFloat(statutoryInfo.max_contribution));
        }
        return amount;
      }

      return parseFloat(deduction.amount || 0);
    },

    calculateTotalDeductions() {
      const nssf = this.calculateNSSFAmount(this.salaryForm.basic_salary);
      const shif = this.calculateSHIF();
      const housingLevy = this.calculateHousingLevy();
      const paye = this.getNetPAYE();
      const others = this.calculateOtherDeductions();

      return nssf + shif + housingLevy + paye + others;
    },

    calculateNSSFAmount(basicSalary) {
      const salary = parseFloat(basicSalary);

      const nssfDeductions = Object.values(this.deductionTypesMap).filter(d =>
          d.deduction_name.includes('NSSF') && !d.deduction_name.includes('Employer')
      );

      if (nssfDeductions.length === 0) {
        return Math.min(salary, 108000) * 0.06;
      }

      const explicitTierDeductions = nssfDeductions.filter(d => d.deduction_name.includes('Tier'));
      const deductionsToApply = explicitTierDeductions.length > 0 ? explicitTierDeductions : nssfDeductions;

      deductionsToApply.sort((a, b) => {
        const aTier = a.deduction_name.includes('Tier I') || a.deduction_name.includes('Tier 1') ? 1 : 2;
        const bTier = b.deduction_name.includes('Tier I') || b.deduction_name.includes('Tier 1') ? 1 : 2;
        return aTier - bTier;
      });

      let totalNSSF = 0;

      deductionsToApply.forEach(tierDeduction => {
        const minLimit = parseFloat(tierDeduction.min_income_limit || 0);
        const maxLimit = parseFloat(tierDeduction.max_income_limit || Infinity);
        const rate = parseFloat(tierDeduction.default_percentage || 6) / 100;
        const maxContribution = parseFloat(tierDeduction.max_contribution || Infinity);

        if (salary > minLimit) {
          const applicableSalary = Math.min(salary, maxLimit) - minLimit;
          const contribution = Math.min(applicableSalary * rate, maxContribution);
          totalNSSF += contribution;
        }
      });

      return Math.min(totalNSSF, 6480);
    },

    calculateTieredDeduction(deductionName, grossSalary) {
      const deductionInfo = this.deductionTypesMap[deductionName];

      if (!deductionInfo || !deductionInfo.tiers) return 0;

      for (const tier of deductionInfo.tiers) {
        const minAmount = parseFloat(tier.min_amount);
        const maxAmount = tier.max_amount ? parseFloat(tier.max_amount) : Infinity;

        if (grossSalary >= minAmount && grossSalary <= maxAmount) {
          if (tier.fixed_amount) {
            return parseFloat(tier.fixed_amount);
          } else if (tier.percentage) {
            const baseAmount = parseFloat(tier.fixed_amount || 0);
            const percentageAmount = (grossSalary - minAmount) * (parseFloat(tier.percentage) / 100);
            return baseAmount + percentageAmount;
          }
        }
      }

      return 0;
    },

    calculateNetSalary() {
      const basicSalary = parseFloat(this.salaryForm.basic_salary || 0);
      const totalAllowances = this.calculateTotalAllowances();
      const totalDeductions = this.calculateTotalDeductions();

      return basicSalary + totalAllowances - totalDeductions;
    },

    async addMandatoryDeductions() {
      const effectiveDate = this.salaryForm.effective_date;
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);

      const deductionsToAdd = this.mandatoryDeductions.filter(deduction =>
          !currentDeductionTypeIds.includes(deduction.id)
      );

      const promises = deductionsToAdd.map(async (deduction) => {
        const calculationMethod = this.deductionTypesMap[deduction.deduction_name]?.calculation_method || deduction.calculation_method;

        const deductionData = {
          deduction_type_id: deduction.id,
          is_percentage: calculationMethod === 'Percentage',
          percentage: deduction.default_percentage,
          amount: deduction.default_amount,
          effective_date: effectiveDate
        };

        if (calculationMethod === 'Tiered' || calculationMethod === 'Custom') {
          deductionData.percentage = null;
          deductionData.amount = null;
          deductionData.is_percentage = false;
        } else if (calculationMethod === 'Fixed') {
          deductionData.is_percentage = false;
          deductionData.percentage = null;
        }

        return await this.addMandatoryDeduction(deductionData);
      });

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Error adding mandatory deductions:', error);
        throw error;
      }
    },

    async addMandatoryDeduction(deductionData) {
      const data = new FormData();
      data.append("function", "addEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_type_id", deductionData.deduction_type_id);
      data.append("effective_date", deductionData.effective_date);
      data.append("is_mandatory", "1");

      if (deductionData.is_percentage) {
        data.append("is_percentage", "1");
        if (deductionData.percentage !== null) {
          data.append("percentage", deductionData.percentage);
        }
      } else {
        data.append("is_percentage", "0");
        if (deductionData.amount !== null) {
          data.append("amount", deductionData.amount);
        }
      }

      try {
        const res = await execute(data, authUrl);
        if (!res.data.success) {
          console.error(`Failed to add mandatory deduction ${deductionData.deduction_type_id}:`, res.data.message);
        }
        return res;
      } catch (error) {
        console.error(`Error adding mandatory deduction ${deductionData.deduction_type_id}:`, error);
        throw error;
      }
    },

    async saveSalary() {
      if (!this.employeeData.id) return;

      if (!this.salaryForm.basic_salary || !this.salaryForm.effective_date) {
        alert("Please fill in all required fields (Basic Salary and Effective Date)");
        return;
      }

      this.submitLoading = true;

      const timeoutId = setTimeout(() => {
        if (this.submitLoading) {
          this.submitLoading = false;
          alert("Request timed out. Please try again.");
        }
      }, 30000);

      try {
        if (!this.hasExistingSalary || this.needsToAddMandatoryDeductions) {
          await this.addMandatoryDeductions();
        }

        const data = new FormData();
        data.append("function", "updateEmployeeSalary");
        data.append("employeeId", this.employeeData.id);
        data.append("basic_salary", this.salaryForm.basic_salary);
        data.append("effective_date", this.salaryForm.effective_date);

        if (this.salaryForm.pay_grade_id) {
          data.append("pay_grade_id", this.salaryForm.pay_grade_id);
        }

        data.append("payment_frequency", this.salaryForm.payment_frequency);
        data.append("currency", this.salaryForm.currency);
        data.append("payment_method", this.salaryForm.payment_method);
        data.append("is_active", this.salaryForm.is_active ? 1 : 0);

        if (this.salaryForm.bank_name) data.append("bank_name", this.salaryForm.bank_name);
        if (this.salaryForm.bank_code) data.append("bank_code", this.salaryForm.bank_code);
        if (this.salaryForm.account_number) data.append("account_number", this.salaryForm.account_number);
        if (this.salaryForm.branch_code) data.append("branch_code", this.salaryForm.branch_code);
        if (this.salaryForm.mobile_money_number) data.append("mobile_money_number", this.salaryForm.mobile_money_number);
        if (this.salaryForm.notes) data.append("notes", this.salaryForm.notes);

        const res = await execute(data, authUrl);

        if (res.data.success) {
          alert(res.data.message || "Salary updated successfully");
          this.fetchEmployeeSalary(this.employeeData.id);
          this.isEditMode = false;
          this.$emit('salary-updated');
        } else {
          alert(res.data.message || "Failed to update salary");
        }
      } catch (error) {
        console.error("API Error:", error);
        alert("An error occurred while updating salary: " + (error.message || "Unknown error"));
      } finally {
        clearTimeout(timeoutId);
        this.submitLoading = false;
      }
    },

    addAllowance() {
      if (!this.isAllowanceFormValid || !this.employeeData.id) return;

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", "addEmployeeAllowance");
      data.append("employee_id", this.employeeData.id);
      data.append("allowance_type_id", this.allowanceForm.allowance_type_id);
      data.append("effective_date", this.allowanceForm.effective_date);

      if (this.allowanceForm.amount_type === 'fixed') {
        data.append("amount", this.allowanceForm.amount);
        data.append("is_percentage", 0);
      } else {
        data.append("percentage", this.allowanceForm.percentage);
        data.append("is_percentage", 1);
      }

      if (this.allowanceForm.end_date) {
        data.append("end_date", this.allowanceForm.end_date);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || "Allowance added successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
              this.hideAddAllowanceModal();
              this.resetAllowanceForm();
            } else {
              alert(res.data.message || "Failed to add allowance");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while adding allowance");
          });
    },

    addDeduction() {
      if (!this.isDeductionFormValid || !this.employeeData.id) return;

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", "addEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_type_id", this.deductionForm.deduction_type_id);
      data.append("effective_date", this.deductionForm.effective_date);

      if (this.deductionForm.amount_type === 'fixed') {
        data.append("amount", this.deductionForm.amount);
        data.append("is_percentage", 0);
      } else {
        data.append("percentage", this.deductionForm.percentage);
        data.append("is_percentage", 1);
      }

      if (this.deductionForm.end_date) {
        data.append("end_date", this.deductionForm.end_date);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || "Deduction added successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
              this.hideAddDeductionModal();
              this.resetDeductionForm();
            } else {
              alert(res.data.message || "Failed to add deduction");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while adding deduction");
          });
    },

    removeAllowance(allowanceId) {
      if (!confirm("Are you sure you want to remove this allowance?")) return;

      const data = new FormData();
      data.append("function", "removeEmployeeAllowance");
      data.append("employee_id", this.employeeData.id);
      data.append("allowance_id", allowanceId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              alert(res.data.message || "Allowance removed successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
            } else {
              alert(res.data.message || "Failed to remove allowance");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            alert("An error occurred while removing allowance");
          });
    },

    removeDeduction(deductionId) {
      if (!confirm("Are you sure you want to remove this deduction?")) return;

      const data = new FormData();
      data.append("function", "removeEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_id", deductionId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              alert(res.data.message || "Deduction removed successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
            } else {
              alert(res.data.message || "Failed to remove deduction");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            alert("An error occurred while removing deduction");
          });
    }
  }
};
</script>

<style scoped>
/* Modal shell */
.esal-dialog {
  max-width: 960px;
}

.esal-modal-content {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
}

.esal-body {
  padding: 20px 24px 24px;
  background: #f8fafc;
}

.esal-content form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Hero */
.esal-hero {
  position: relative;
  padding: 24px;
  overflow: hidden;
}

.esal-hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #a855f7 100%);
}

.esal-hero-inner {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.esal-hero-left {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  min-width: 0;
}

.esal-avatar {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.95);
  color: #4f46e5;
  font-size: 22px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 3px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.esal-hero-label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.75);
}

.esal-hero-name {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

.esal-hero-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.esal-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
  backdrop-filter: blur(4px);
}

.esal-hero-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.esal-close {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.esal-close.dark {
  background: #f1f5f9;
  color: #64748b;
}

/* Buttons */
.esal-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.esal-btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.esal-btn-light {
  background: rgba(255, 255, 255, 0.95);
  color: #4f46e5;
}

.esal-btn-ghost {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.esal-btn-ghost-dark {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.esal-btn-primary,
.esal-btn.primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.esal-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.esal-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.esal-btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: esal-spin 0.7s linear infinite;
}

@keyframes esal-spin {
  to { transform: rotate(360deg); }
}

/* Loading */
.esal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px;
  color: #64748b;
}

.esal-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: esal-spin 0.7s linear infinite;
}

/* Notices */
.esal-notice {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 13px;
}

.esal-notice i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.esal-notice p,
.esal-notice ul {
  margin: 4px 0 0;
  color: #64748b;
  line-height: 1.5;
}

.esal-notice ul {
  padding-left: 18px;
}

.esal-notice.info {
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  color: #3730a3;
}

.esal-notice.success {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.esal-notice.compact {
  padding: 12px 14px;
}

/* Panels */
.esal-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 768px) {
  .esal-grid-2 {
    grid-template-columns: 1fr;
  }
}

.esal-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.esal-panel-wide {
  grid-column: 1 / -1;
}

.esal-panel-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.esal-panel-head h3 {
  margin: 0;
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.esal-panel-head i {
  font-size: 18px;
  color: #6366f1;
}

.esal-panel-body {
  padding: 18px;
}

.esal-panel-body-flush {
  padding: 0;
}

/* Form fields */
.esal-field {
  margin-bottom: 14px;
}

.esal-field:last-child {
  margin-bottom: 0;
}

.esal-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.esal-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
}

.esal-req {
  color: #dc2626;
}

.esal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.esal-input:focus {
  outline: none;
  border-color: #818cf8;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.2);
}

.esal-input:read-only,
.esal-input:disabled,
.esal-select:disabled {
  background: #f8fafc;
  color: #64748b;
}

.esal-textarea {
  resize: vertical;
  min-height: 88px;
}

.esal-input-group {
  display: flex;
  align-items: stretch;
}

.esal-input-prefix {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-right: none;
  border-radius: 10px 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.esal-input-group .esal-input {
  border-radius: 0 10px 10px 0;
}

.esal-hint {
  margin: 6px 0 0;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.esal-hint.success { color: #059669; }
.esal-hint.warn { color: #d97706; }
.esal-hint.danger { color: #dc2626; }

.esal-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
}

/* Pay grade bar */
.esal-grade-desc {
  margin: 0 0 14px;
  font-size: 13px;
  color: #64748b;
}

.esal-grade-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.esal-grade-stat span {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.esal-grade-stat strong {
  font-size: 15px;
  color: #1e293b;
}

.esal-grade-bar-track {
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.esal-grade-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

.esal-grade-bar-fill.bg-success { background: linear-gradient(90deg, #10b981, #34d399); }
.esal-grade-bar-fill.bg-warning { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.esal-grade-bar-fill.bg-info { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.esal-grade-bar-fill.bg-danger { background: linear-gradient(90deg, #ef4444, #f87171); }
.esal-grade-bar-fill.bg-secondary { background: #94a3b8; }

.esal-grade-bar-label {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}

/* Tables */
.esal-table-wrap {
  overflow-x: auto;
}

.esal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.esal-table th,
.esal-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
}

.esal-table th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  background: #fafbfc;
}

.esal-muted {
  color: #94a3b8;
}

/* Item lists */
.esal-item-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.esal-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.esal-item-row strong {
  display: block;
  font-size: 13px;
  color: #1e293b;
  margin-bottom: 4px;
}

.esal-item-row-end {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.esal-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  margin-right: 4px;
}

.esal-pill.green { background: #d1fae5; color: #065f46; }
.esal-pill.amber { background: #fef3c7; color: #b45309; }
.esal-pill.red { background: #fee2e2; color: #991b1b; }

.esal-amount {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.esal-amount.earn { color: #059669; }
.esal-amount.ded { color: #dc2626; }
.esal-amount.muted { opacity: 0.6; font-style: italic; }

.esal-icon-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.esal-icon-btn.danger:hover {
  background: #fee2e2;
  color: #dc2626;
}

.esal-lock {
  color: #cbd5e1;
  font-size: 16px;
}

.esal-empty-inline {
  text-align: center;
  padding: 24px;
  color: #94a3b8;
  font-size: 13px;
}

.esal-empty-inline i {
  display: block;
  font-size: 28px;
  margin-bottom: 6px;
  opacity: 0.5;
}

/* Breakdown */
.esal-breakdown-grid {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 20px;
}

@media (max-width: 768px) {
  .esal-breakdown-grid {
    grid-template-columns: 1fr;
  }
}

.esal-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px solid #f8fafc;
}

.esal-line span {
  color: #64748b;
}

.esal-line strong {
  color: #1e293b;
}

.esal-line.earn strong { color: #059669; }
.esal-line.ded strong { color: #dc2626; }

.esal-line.total {
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 4px;
}

.esal-line.total span,
.esal-line.total strong {
  font-weight: 700;
  color: #1e293b;
}

.esal-line-section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  padding: 12px 0 4px;
}

.esal-net-card {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-radius: 16px;
  padding: 20px;
  color: #fff;
  text-align: center;
  margin-bottom: 12px;
}

.esal-net-label {
  display: block;
  font-size: 12px;
  opacity: 0.85;
  margin-bottom: 6px;
}

.esal-net-value {
  display: block;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.esal-net-sub {
  display: block;
  font-size: 11px;
  opacity: 0.7;
  margin-top: 6px;
}

/* Footer */
.esal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

/* Sub-modals */
.esal-sub-content {
  border: none;
  border-radius: 16px;
  overflow: hidden;
}

.esal-sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.esal-sub-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.esal-sub-body {
  padding: 20px;
}

.esal-sub-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
}

/* Legacy form in sub-modals */
.esal-sub-body .form-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.esal-sub-body .form-control,
.esal-sub-body .form-select {
  border-radius: 10px;
  border-color: #e2e8f0;
  padding: 10px 12px;
}

/* Pay grade select options */
select option.bg-light-success {
  background-color: #d1fae5;
  color: #065f46;
  font-weight: 500;
}

:deep(#employeeSalaryModal),
:deep(#employeeSalaryAddAllowanceModal),
:deep(#employeeSalaryAddDeductionModal) {
  z-index: 20060 !important;
}

:deep(.modal-backdrop) {
  z-index: 20050 !important;
}
</style>